import { isLocalTursoUrl } from "@-/com.npmjs.libsql__client/is-local-turso-url";
import type { DeviceAccount } from "@-/dev.improve.haniverse.lib.device-settings";
import { ACTOR_SYNC_PROTOCOL } from "@-/dev.improve.haniverse.lib.database.actor.migration-tags";

/**
 * Resolve the actor Turso sync URL to open the connection against, given the stored device account.
 *
 * WHY this isn't just `account.tursoDbSyncUrl`: in LOCAL DEV the actor sync server is an ephemeral
 * `tursodb --sync-server` bound to a dev-stack loopback port (`http://127.0.0.1:<P+3>`, e.g. 4576 for
 * the default 4573 stack, 5703 for a `--port 5700` stack). That port is minted by whichever dev stack
 * was live at provisioning time and dies with it — so a URL persisted into the device account outlives
 * its server and the app keeps pulling from a dead port (`Could not connect to the server. …/pull-updates`).
 *
 * The fix: in dev we DON'T persist the URL at all (see `deviceAccountSchema` — `tursoDbSyncUrl` is
 * optional and dev provisioning omits it). Instead the dev process inlines the LIVE stack's actor URL
 * as `EXPO_PUBLIC_HANIVERSE_LOCAL_TURSO_SYNC_URL` (device-facing: loopback for host-local sims, the public
 * tunnel host when tunneling), which Metro bakes into the bundle it is currently serving — so it can
 * never point at a stale stack. This resolver prefers that env value whenever the stored URL is absent
 * or is itself a local/loopback URL (a leftover from before this change).
 *
 * In production/staging the env is unset and the account carries the server-authoritative cloud URL
 * (`user_database.tursoDbUrl`, which never changes), so that persisted URL is returned unchanged —
 * production behavior is identical to before.
 *
 * Returns `null` when no URL can be resolved (dev env unset AND no persisted URL): the caller treats
 * that as "cannot open in sync mode" rather than opening against a bogus URL.
 */
export function resolveActorSyncUrl(account: DeviceAccount): string | null {
  const storedUrl = account.tursoDbSyncUrl;

  // A persisted CLOUD (non-local) URL is the source of truth (prod/staging) — return it as-is, even
  // if the dev env happens to be set. Only local/loopback stored URLs (or an absent one) defer to env.
  if (storedUrl != null && !isLocalTursoUrl(storedUrl)) {
    return storedUrl;
  }

  // Dev: the LIVE stack's actor URL, inlined by the dev process (never stale). Trimmed because an
  // empty inlined value reads as "" (varlock), which we treat as unset.
  const devEnvUrl = process.env.EXPO_PUBLIC_HANIVERSE_LOCAL_TURSO_SYNC_URL?.trim();
  if (devEnvUrl !== undefined && devEnvUrl.length > 0) {
    return devEnvUrl;
  }

  // No env override (or not dev). Fall back to whatever local URL was persisted — better a possibly
  // stale local URL than none (e.g. a dev build without the env inlined). `null` if nothing at all.
  return storedUrl ?? null;
}

/**
 * Build the `DeviceAccount` to PERSIST after provisioning, given the `{ token, url }` the server's
 * `database.actor.ensure` returned.
 *
 * The other half of the policy in `resolveActorSyncUrl`: a LOCAL/loopback dev URL is deliberately
 * NOT persisted (it's ephemeral — dies with the dev stack that minted it), so the account carries
 * only the token and the open path re-derives the URL from the live-stack env. A CLOUD
 * (prod/staging) URL is server-authoritative and stable, so it IS persisted and read back at open.
 */
export function buildProvisionedDeviceAccount({
  ownerId,
  token,
  url,
}: {
  ownerId: string;
  token: string;
  url: string;
}): DeviceAccount {
  const account: DeviceAccount = {
    actorSyncProtocol: ACTOR_SYNC_PROTOCOL,
    ownerId,
    tursoDbAuthToken: token,
  };
  return isLocalTursoUrl(url) ? account : { ...account, tursoDbSyncUrl: url };
}

const TRAILING_SLASHES = /\/+$/;

/**
 * Where this device reaches the sync gateway: under the API origin it already talks to
 * (`getBetterAuthBaseUrl()`). Resolved at every open and never stored, and never taken from the
 * server either.
 *
 * A stored address is a way for sync to stay broken: it outlives a dev stack's port, and on the
 * deployed server the request URL names one immutable deployment, so a device that kept it would
 * talk to that old server for good and never see a fix or a new migration. A device that cannot
 * reach the gateway only retries; nothing would ever send it for a new address.
 */
export function resolveActorSyncGatewayUrl(apiBaseUrl: string): string {
  return `${apiBaseUrl.replace(TRAILING_SLASHES, "")}/api/actor-sync`;
}

/**
 * Whether the stored account can sync as it is: its credential was issued for the protocol this
 * build speaks. Anything else is provisioned again, which only replaces the credential.
 */
export function isActorSyncProvisioned(account: DeviceAccount | null): boolean {
  return account?.actorSyncProtocol === ACTOR_SYNC_PROTOCOL;
}

/**
 * An account stored by a build from before the sync gateway protocol. Its credential is not
 * accepted any more and its replica predates the baseline schema, so both are dropped and the
 * device is provisioned again from its session (see `database.md`, "Clean baseline and identity").
 * A later protocol bump does NOT make an account legacy: that one keeps its replica.
 */
export function isLegacyDeviceAccount(account: DeviceAccount | null): boolean {
  return account !== null && account.actorSyncProtocol === undefined;
}
